import { useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { toast } from 'sonner'

import { getSocket } from '@/api/socket'

import { fallbackRenderer, notificationRenderers } from './renderers'
import { installNotificationSocketHandlers } from './socket-handlers'

/** called once at app boot inside the authenticated tree (RegisterUser).
 * installs the notification:* listeners on the shared socket singleton
 * and detaches them on unmount. does NOT disconnect the socket itself:
 * messaging's bootstrap shares the same singleton, so disconnecting
 * here would tear down handlers it just installed. the socket lives
 * for the duration of the authed session.
 *
 * sits inside RegisterUser specifically because:
 * - the socket needs the Better Auth cookie, which only exists after sign-in
 * - the QueryClient is needed for cache updates from socket events
 * - the sonner Toaster is mounted at the app root, ready to receive toast() calls
 */
export function useNotificationBootstrap(): void {
  const queryClient = useQueryClient()

  useEffect(() => {
    const socket = getSocket()
    const detach = installNotificationSocketHandlers(
      socket,
      queryClient,
      notification => {
        const renderer = (notificationRenderers[
          notification.type as keyof typeof notificationRenderers
        ] ?? fallbackRenderer) as {
          title: (payload: unknown) => string
          description: (payload: unknown) => string
        }
        toast(renderer.title(notification.payload), {
          description: renderer.description(notification.payload)
        })
      }
    )
    return () => {
      detach()
    }
  }, [queryClient])
}
